/**
 * GatewayRedirectGuard
 *
 * Wrapper component that ensures the user has an active context selected.
 * If not, redirects to the /select gateway page.
 * Used inside ProtectedPage to guard all app routes.
 */

import { ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/core/auth/useAuth';
import { getAccessibleLocationIds, hasAllLocationAccess } from '@/core/auth/permissions';
import { useActiveContext } from '@/shared/hooks/useActiveContext';

interface GatewayRedirectGuardProps {
  children: ReactNode;
}

/**
 * Paths that don't require a scope context — global surfaces like
 * "My profile", notifications, GDPR exports. The guard must NOT
 * redirect users away from these even when activeContext is null,
 * otherwise opening "My profile" from the user menu bounces them
 * back to /select.
 */
const GLOBAL_PATH_PREFIXES = [
  '/profile',
  '/notifications',
  '/settings/notifications',
  '/help',
  '/logout',
];

function isGlobalPath(pathname: string): boolean {
  return GLOBAL_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export function GatewayRedirectGuard({ children }: GatewayRedirectGuardProps) {
  const { user, isAuthenticated } = useAuth();
  const { activeContext, setActiveContext } = useActiveContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onGlobalPath = isGlobalPath(pathname);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    // Already has context → allow
    if (activeContext) return;
    // Global surfaces (profile, help, notifications…) render fine
    // without a scope context. Don't bounce them to /select.
    if (onGlobalPath) return;

    const canAccessCompany = user.companyAccess;
    const canAccessAllLocations = hasAllLocationAccess(user);
    const accessibleLocationIds = getAccessibleLocationIds(user);
    const locationCount = accessibleLocationIds.length;

    if (!canAccessCompany && !canAccessAllLocations && locationCount === 1) {
      // Auto-select single location
      setActiveContext({ type: 'location', id: accessibleLocationIds[0] });
      return;
    }

    // Multiple locations or company level → redirect to gateway
    navigate('/select', { replace: true });
  }, [isAuthenticated, user, activeContext, setActiveContext, navigate, onGlobalPath]);

  // While no context and multi-location user, don't render children —
  // except on global paths, which always render. Otherwise the
  // children mount, fetch scope-dependent data with null filter, and
  // flash an empty state before the redirect kicks in.
  if (
    !onGlobalPath
    && !activeContext
    && user
    && (
      user.companyAccess
      || hasAllLocationAccess(user)
      || getAccessibleLocationIds(user).length > 1
    )
  ) {
    return null;
  }

  return <>{children}</>;
}
